// Client portal Storage tab — read-only mirror of the admin Storage tab.
// Renders the rate card, the entries, and the per-week billing calendar
// for the current period. All data pulled from /me/* endpoints.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertTriangle } from 'lucide-react'
import {
  getMeStorageRates, getMeStorageEntries, getMeStoragePreview,
} from '../../../lib/api'
import { formatCurrency, formatInt, formatDate } from './helpers'

const TYPE_LABELS = {
  controlled_temp_pallet: 'Controlled Temp Pallet',
  ambient_pallet:         'Ambient Pallet',
  controlled_temp_bin:    'Controlled Temp Bin',
  ambient_bin:            'Ambient Bin',
}

function nowYM() {
  const d = new Date()
  return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

export default function StorageTab({ clientId }) {
  const [{ year, month }, setPeriod] = useState(nowYM())

  const { data: rates }    = useQuery({
    queryKey: ['me', 'storage', 'rates', clientId],
    queryFn:  () => getMeStorageRates(clientId),
    enabled:  !!clientId,
  })
  const { data: entries = [], error: entriesError } = useQuery({
    queryKey: ['me', 'storage', 'entries', clientId],
    queryFn:  () => getMeStorageEntries(clientId),
    enabled:  !!clientId,
  })
  const { data: preview, isLoading: previewLoading, error: previewError } = useQuery({
    queryKey: ['me', 'storage', 'preview', clientId, year, month],
    queryFn:  () => getMeStoragePreview(clientId, { year, month }),
    enabled:  !!clientId,
  })

  // Order oldest → newest by started_on so the table reads as a timeline
  // (Apr at the top, May at the bottom). Backend doesn't guarantee order;
  // sort here so the UI is stable regardless of insertion sequence.
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const sa = a.started_on || ''
      const sb = b.started_on || ''
      return sa < sb ? -1 : sa > sb ? 1 : 0
    })
  }, [entries])

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(
    'en-AU', { year: 'numeric', month: 'long' },
  )

  return (
    <div className="space-y-6">
      {/* Rate card */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Storage rates (per unit per week)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <div key={key} className="flex justify-between border-b border-slate-100 py-1.5">
              <span className="text-slate-500">{label}</span>
              <span className="text-slate-800 font-medium tabular-nums">
                {formatCurrency(rates?.[`${key}_rate`])}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Entries */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Storage entries</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Active and historical entries. Quantity adjustments are recorded
            as a new entry continuing the previous one.
          </p>
        </div>
        {/* `entries = []` makes a failed fetch look like "nothing on file",
            i.e. nothing stored and nothing being charged for. */}
        {entriesError ? (
          <div className="p-6 text-center text-sm">
            <AlertTriangle size={20} className="mx-auto text-red-400 mb-1.5" />
            <p className="font-medium text-red-600">Failed to load storage entries</p>
            <p className="text-xs text-red-500 mt-0.5">
              This is a load failure, <strong>not</strong> an empty list — entries may exist on file.
            </p>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              {entriesError?.response?.data?.detail || entriesError?.message || 'Unknown error'}
            </p>
          </div>
        ) : sortedEntries.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No storage entries on file.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5 text-right">Qty</th>
                  <th className="px-4 py-2.5">Started</th>
                  <th className="px-4 py-2.5">Ended</th>
                  <th className="px-4 py-2.5">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedEntries.map((e, i) => (
                  <tr key={e.id || i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                    <td className="px-4 py-2 text-slate-700">{TYPE_LABELS[e.storage_type] || e.storage_type}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">{formatInt(e.quantity)}</td>
                    <td className="px-4 py-2 text-slate-600">{formatDate(e.started_on)}</td>
                    <td className="px-4 py-2 text-slate-600">{e.ended_on ? formatDate(e.ended_on) : <span className="text-slate-300">— active</span>}</td>
                    <td className="px-4 py-2 text-slate-500">{e.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Billing calendar */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Billing Calendar</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Per-week storage charges for the selected period.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setPeriod(({ year, month }) => {
                const m = month === 1 ? 12 : month - 1
                const y = month === 1 ? year - 1 : year
                return { year: y, month: m }
              })}
              className="px-2 py-1 rounded text-slate-500 hover:bg-slate-100"
            >‹</button>
            <span className="text-slate-700 font-medium min-w-[90px] text-center">{monthLabel}</span>
            <button
              onClick={() => setPeriod(({ year, month }) => {
                const m = month === 12 ? 1 : month + 1
                const y = month === 12 ? year + 1 : year
                return { year: y, month: m }
              })}
              className="px-2 py-1 rounded text-slate-500 hover:bg-slate-100"
            >›</button>
          </div>
        </div>

        {previewLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : previewError ? (
          /* An undefined `preview` reads as "no billable storage", i.e. a $0
             period, which is a charge claim and not an empty result. */
          <div className="p-6 text-center text-sm">
            <AlertTriangle size={20} className="mx-auto text-red-400 mb-1.5" />
            <p className="font-medium text-red-600">Failed to load the billing calendar</p>
            <p className="text-xs text-red-500 mt-0.5">
              This is a load failure, <strong>not</strong> a $0 period — billable storage may exist
              for {monthLabel}.
            </p>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              {previewError?.response?.data?.detail || previewError?.message || 'Unknown error'}
            </p>
          </div>
        ) : !preview?.types?.length ? (
          <div className="p-6 text-center text-sm text-slate-400">
            No billable storage in this period.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {preview.types.map((t) => (
              <div key={t.storage_type} className="px-4 py-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-slate-700">{TYPE_LABELS[t.storage_type] || t.storage_type}</h4>
                  <span className="text-xs text-slate-400">
                    anchored {formatDate(t.anchor_date)} · {formatDate(t.window_start)} – {formatDate(t.window_end)}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium pb-1">Week</th>
                      <th className="text-right font-medium pb-1">Qty</th>
                      <th className="text-right font-medium pb-1">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(t.weeks || []).map((w) => (
                      <tr key={w.week_number}>
                        <td className="py-1.5 text-slate-700">
                          Wk {w.week_number}
                          <span className="text-slate-400 ml-2 text-xs">{formatDate(w.week_start)} – {formatDate(w.week_end)}</span>
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-slate-600">{formatInt(w.quantity)}</td>
                        <td className="py-1.5 text-right tabular-nums text-slate-700">{formatCurrency(w.cost)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50">
                      <td className="py-1.5 text-right font-semibold text-slate-600 text-xs uppercase tracking-wider">Type Total</td>
                      <td />
                      <td className="py-1.5 text-right tabular-nums font-bold text-slate-800">{formatCurrency(t.type_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
            <div className="px-4 py-3 bg-slate-50 flex justify-end">
              <span className="text-sm font-semibold text-slate-700">
                Period Total: <span className="text-slate-900 tabular-nums">{formatCurrency(preview.grand_total)}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
